import classnames from "classnames";
import React from "react";
import { Hyperlink, LazyloadImage, ProductAddButton } from "src/components";
import { mapPrismicProductToProduct } from "src/services/mapperService";
import { translate } from "src/services/translation.service";
import theme, { media } from "src/theme";
import styled from "styled-components";

import { LinkButton, RichTextBlock, Slice } from "../components";
import { IItemsFocusProducts, ISliceFocusProducts } from "../types";

const TextBlock = styled(RichTextBlock)`
  text-align: center;
`;

const Image = styled.img`
  display: block;
  margin: auto;
  max-width: 100%;
  height: auto;
`;

const StyledProductAddButton = styled(ProductAddButton)`
  display: inline-block;
  width: initial;
  margin-top: ${theme.margins.marginBase};

  button {
    margin-top: 0;
  }
`;

interface IColumns {
  columnCount: number;
  columnCountMobile: number;
  columnReverse?: boolean;
  columnReverseMobile?: boolean;
}

const Grid = styled.div<IColumns>`
  display: grid;
  justify-content: center;
  align-content: baseline;
  grid-gap: 10px;

  direction: ${({ columnReverseMobile }: IColumns): string | undefined =>
    columnReverseMobile ? "rtl" : undefined};

  ${({ columnCountMobile }: IColumns): string | undefined =>
    columnCountMobile
      ? `
      grid-template-columns: repeat(${columnCountMobile}, 1fr);
    `
      : undefined};

  ${media.mobile} {
    direction: ${({ columnReverse }: IColumns): string | undefined =>
      columnReverse ? "rtl" : "initial"};

    ${({ columnCount }: IColumns): string | undefined =>
      columnCount
        ? `
        display: grid;
        grid-template-columns: repeat(${columnCount}, 1fr);
      `
        : undefined};
  }
`;

const GridRow = styled(Grid)`
  justify-items: center;
  align-items: center;
`;

interface IColumn {
  columnAlternate?: boolean;
  columnAlternateMobile?: boolean;
  columnOrder?: number;
}

const Column = styled.article<IColumn>`
  order: ${({ columnOrder, columnAlternateMobile }: IColumn): number | string =>
    columnOrder && columnAlternateMobile ? columnOrder : "initiale"};

  ${media.mobile} {
    order: ${({ columnOrder, columnAlternate }: IColumn): number | string =>
      columnOrder && columnAlternate ? columnOrder : "initial"};
  }
`;

export const FocusProducts = ({
  slice_type,
  primary,
  items,
}: ISliceFocusProducts): JSX.Element | null => {
  const {
    class_name: className,
    background_color: backgroundColor,
    background_image: backgroundImage,
    column_count: columnCount,
    column_count_mobile: columnCountMobile,
    column_alternate: columnAlternate,
    column_alternate_mobile: columnAlternateMobile,
    column_reverse: columnReverse,
    column_reverse_mobile: columnReverseMobile,
    rich_text_top: richTextTop,
    rich_text_bottom: richTextBottom,
  } = primary;

  return (
    <Slice
      className={classnames("slice-focus-products", className)}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      noGutter
    >
      <TextBlock className="focus-products-text-intro" render={richTextTop} />
      <Grid
        className="grid-products"
        columnCount={columnCount || 1}
        columnCountMobile={columnCountMobile || 1}
      >
        {items.map(
          (
            {
              focus_product: rawProduct,
              focus_product_image: productImage,
              focus_product_rich_text: productText,
              focus_product_cta: productCta,
              focus_product_cta_label: productCtaLabel,
              focus_product_cta_url: productCtaUrl,
              focus_product_column_count: productColumnCount,
              focus_product_column_count_mobile: productColumnCountMobile,
            }: IItemsFocusProducts,
            i
          ) => {
            const product =
              rawProduct !== undefined && rawProduct !== null
                ? mapPrismicProductToProduct([rawProduct])[0]
                : undefined;

            /**
             * CTA
             * Show button addToCart or link
             */
            const cta = (): JSX.Element | null => {
              if (productCta === (undefined || null)) {
                return null;
              }

              if (
                product &&
                (productCta === "cart" || productCta === "cartQuantity")
              ) {
                return (
                  <StyledProductAddButton
                    product={product}
                    label={productCtaLabel}
                    canBeAddedToCart={true}
                    canEditQuantity={productCta === "cartQuantity"}
                  />
                );
              }

              if (productCta === "link") {
                return (
                  <LinkButton to={productCtaUrl || product?.url}>
                    {productCtaLabel || translate("global.seeMore")}
                  </LinkButton>
                );
              }

              return null;
            };

            return (
              <GridRow
                className="grid-product"
                columnReverse={columnReverse}
                columnReverseMobile={columnReverseMobile}
                columnCount={productColumnCount || 2}
                columnCountMobile={productColumnCountMobile || 1}
                key={i}
              >
                <Column>
                  {productImage && (
                    <Hyperlink to={productCtaUrl || product?.url}>
                      <LazyloadImage height={productImage.dimensions?.height}>
                        <Image src={productImage.url} alt={product?.name} />
                      </LazyloadImage>
                    </Hyperlink>
                  )}
                </Column>

                <Column
                  columnAlternate={columnAlternate}
                  columnAlternateMobile={columnAlternateMobile}
                  columnOrder={i % 2 !== 0 ? -1 : undefined}
                >
                  <RichTextBlock render={productText} />
                  {cta()}
                </Column>
              </GridRow>
            );
          }
        )}
      </Grid>
      <TextBlock
        className="focus-products-text-outro"
        render={richTextBottom}
      />
    </Slice>
  );
};
